import { useState, useEffect, useRef } from 'react';
import { auth } from '../FirebaseAuthConfig';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';

// These don't need to be inside the component
const countryCode = '+1';
const normalizePhoneNumber = (phone) => {
    const phoneWithoutCountryCode = phone.replace(/[^0-9]+/g, '').slice(-10);
    if (phoneWithoutCountryCode.length < 10) {
        return; // assuming US/CA phone numbers, return early if it's not valid
    }
    return `${countryCode}${phoneWithoutCountryCode}`; // now add country code
};

function AuthWithPhone({ onVerified, cancelled }) {
    //
    const [phoneNumber, setPhoneNumber] = useState('');
    const [confResult, setConfResult] = useState(null);
    const [OTP, setOTP] = useState('');

    // No need for "window.appVerifier" since we're using react, just set up the Captcha on mount
    const [appVerifier, setAppVerifier] = useState(null);
    const captchaRef = useRef();

    useEffect(() => {
        if (!captchaRef.current) return;
        setAppVerifier(new RecaptchaVerifier(captchaRef.current, { size: 'invisible' }, auth));
    }, []); // empty array == "run once, after mounting"

    const requestOTP = (e) => {
        e && e.preventDefault();
        // See notes above
        const normalizedPhone = normalizePhoneNumber(phoneNumber);
        if (!normalizedPhone) return; // not valid, no action needed
        signInWithPhoneNumber(auth, normalizedPhone, appVerifier)
            .then((result) => setConfResult(result))
            .catch((error) => {
                // TODO: handle error in UI (error/setError)
                console.log(error);
            });
    };

    const secondRequestOTP = (e) => {
        requestOTP(e); // same as above
    };

    const verifyOTP = (e) => {
        e.preventDefault();
        if (!confResult || OTP.length < 6) return; // return early
        confResult.confirm(OTP).then((result) => {
            onVerified(result.user);
        });
    };

    return (
        <>
            {!confResult ? (
                <form onSubmit={requestOTP}>
                    <div className="w-96 h-fit gap-2 flex flex-col justify-center items-center">
                        <h3 className="w-fit font-sans font-normal text-base text-white">{'Please enter your Phone Number.'}</h3>
                        <input
                            type="tel"
                            className="w-full h-12 px-4 border-none rounded-2xl bg-white font-sans text-xl font-semibold tracking-[0.1rem] text-[#202020] text-center"
                            id="phoneNumberInput"
                            value={phoneNumber}
                            placeholder="Phone Number"
                            onChange={(e) => setPhoneNumber(e.target.value)}
                        />
                        <div className="w-full h-12 flex flex-row justify-center items-center gap-2">
                            <button
                                type="button"
                                className="h-full aspect-square border-none text-white cursor-pointer flex justify-center items-center"
                                onClick={() => cancelled(true)}
                            >
                                <svg viewBox="0 0 260 260" className="w-1/2 h-1/2 fill-current">
                                    <path
                                        d="M53.393,129.567c0,6.241 2.28,11.384 7.403,16.429l110.514,107.452c3.837,3.924 8.598,5.752 14.13,5.752c11.26,0 20.367,-8.98 20.367,-20.166c0,-5.635 -2.317,-10.711 -6.458,-14.872l-97.958,-94.662l97.958,-94.528c4.208,-4.162 6.458,-9.324 6.458,-14.805c0,-11.186 -9.107,-20.166 -20.367,-20.166c-5.598,0 -10.293,1.828 -14.13,5.752l-110.514,107.385c-5.056,5.045 -7.336,10.121 -7.403,16.429Z"
                                        className="fill-current"
                                    />
                                </svg>
                            </button>
                            <button
                                className="w-full h-12 px-4 border-none rounded-2xl bg-[#FF671F] font-sans text-xl font-semibold tracking-[0.1rem] text-white text-center"
                                type="submit"
                            >
                                Get Code
                            </button>
                        </div>
                    </div>
                </form>
            ) : (
                <form onSubmit={verifyOTP}>
                    <div className="w-96 h-fit gap-2 flex flex-col justify-center items-center">
                        <h3 className="font-sans font-normal text-base text-white" id="sentToText">{`Code sent to: (###) ###-${phoneNumber.substring(
                            phoneNumber.length - 4
                        )}`}</h3>
                        <input
                            className="w-full h-12 px-4 border-none rounded-2xl bg-white font-sans text-xl font-semibold tracking-[0.1rem] text-[#202020] text-center"
                            id="otpInput"
                            value={OTP}
                            placeholder="OTP Code"
                            onChange={(e) => setOTP(e.target.value)}
                        />
                        <div className="w-full h-12 flex flex-row justify-center items-center gap-2">
                            <button
                                className="h-full aspect-square border-none text-white cursor-pointer flex justify-center items-center"
                                onClick={() => cancelled(true)}
                            >
                                <svg viewBox="0 0 260 260" className="w-1/2 h-1/2 fill-current">
                                    <path
                                        d="M53.393,129.567c0,6.241 2.28,11.384 7.403,16.429l110.514,107.452c3.837,3.924 8.598,5.752 14.13,5.752c11.26,0 20.367,-8.98 20.367,-20.166c0,-5.635 -2.317,-10.711 -6.458,-14.872l-97.958,-94.662l97.958,-94.528c4.208,-4.162 6.458,-9.324 6.458,-14.805c0,-11.186 -9.107,-20.166 -20.367,-20.166c-5.598,0 -10.293,1.828 -14.13,5.752l-110.514,107.385c-5.056,5.045 -7.336,10.121 -7.403,16.429Z"
                                        className="fill-current"
                                    />
                                </svg>
                            </button>
                            <button
                                className="w-full h-12 px-4 border-none rounded-2xl bg-[#FF671F] font-sans text-xl font-semibold tracking-[0.1rem] text-white text-center"
                                type="submit"
                            >
                                Verify and Register
                            </button>
                        </div>
                        <button
                            className="mt-4 border-none bg-transparent flex flex-row justify-center items-center cursor-pointer"
                            type="button"
                            onClick={() => {
                                secondRequestOTP();
                                // TODO: useState (don't modify the DOM directly)
                                document.getElementById('sentToText').value = `Resent code to: (###) ###-${phoneNumber.substring(phoneNumber.length - 4)}`;
                            }}
                        >
                            <h3 className="font-sans font-normal text-base text-white">
                                Didn't receive a code? <span className="font-semibold">Request Again</span>
                            </h3>
                        </button>
                    </div>
                </form>
            )}
            <div id="recaptcha-container" ref={captchaRef} />
        </>
    );
}
export default AuthWithPhone;
